import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { BadgeCelebrationModal } from "../components/BadgeCelebrationModal";
import { 
  ArrowLeft,
  Clock,
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Award,
  GraduationCap
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const DIFFICULTY_COLORS = {
  beginner: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  intermediate: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  advanced: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

export const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [celebrationBadge, setCelebrationBadge] = useState(null);

  const fetchCourse = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/courses/${courseId}`);
      setCourse(response.data);
      setCompletedLessons(new Set(response.data.completed_lessons || []));
      
      // Find first incomplete lesson
      const lessons = response.data.lessons || [];
      const firstIncomplete = lessons.findIndex(l => !response.data.completed_lessons?.includes(l.id));
      if (firstIncomplete !== -1) {
        setCurrentLessonIndex(firstIncomplete);
      }
    } catch (error) {
      console.error("Failed to fetch course:", error);
      toast.error("Failed to load course");
      navigate("/courses");
    } finally {
      setLoading(false);
    }
  }, [api, courseId, navigate]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  const handleCompleteLesson = async () => {
    if (!course || !course.lessons[currentLessonIndex]) return;
    
    const lesson = course.lessons[currentLessonIndex];
    if (completedLessons.has(lesson.id)) {
      // Already completed, just go to next
      if (currentLessonIndex < course.lessons.length - 1) {
        setCurrentLessonIndex(prev => prev + 1);
      }
      return;
    }

    try {
      const response = await api.post(`/courses/${courseId}/lessons/${lesson.id}/complete`);
      setCompletedLessons(new Set(response.data.completed_lessons));
      
      // Check if user earned any new badges
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        // Show celebration for the first badge (most relevant one)
        setCelebrationBadge(response.data.new_badges[0]);
      } else if (response.data.is_course_complete) {
        toast.success("Congratulations! You've completed the course!", {
          icon: <Award className="w-5 h-5 text-amber-500" />
        });
      } else {
        toast.success("Lesson completed!");
        // Auto-advance to next lesson
        if (currentLessonIndex < course.lessons.length - 1) {
          setTimeout(() => setCurrentLessonIndex(prev => prev + 1), 500);
        }
      }
    } catch (error) {
      toast.error("Failed to save progress");
    }
  };

  const handleCloseCelebration = () => {
    setCelebrationBadge(null);
    // Auto-advance to next lesson after closing celebration
    if (currentLessonIndex < course.lessons.length - 1) {
      setTimeout(() => setCurrentLessonIndex(prev => prev + 1), 300);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="course-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  if (!course) return null;

  const lessons = course.lessons || [];
  const currentLesson = lessons[currentLessonIndex];
  const progress = lessons.length > 0 
    ? Math.round((completedLessons.size / lessons.length) * 100)
    : 0;
  const allComplete = completedLessons.size === lessons.length && lessons.length > 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="course-detail-page">
      {/* Header */}
      <header className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/courses")}
          className="mt-1 shrink-0"
          data-testid="back-to-courses"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Badge variant="secondary" className={DIFFICULTY_COLORS[course.difficulty]}>
              {course.difficulty}
            </Badge>
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {course.duration}
            </span>
          </div>
          <h1 className="font-heading text-xl md:text-2xl font-extrabold tracking-tight">
            {course.title}
          </h1>
          <p className="text-muted-foreground mt-1">{course.description}</p>
        </div>
      </header>

      {/* Progress Bar */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Course Progress</span>
            <span className="text-sm text-muted-foreground">{progress}% complete</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-[280px,1fr] gap-6">
        {/* Lesson Sidebar */}
        <Card className="border-border/50 h-fit lg:sticky lg:top-24">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              Lessons
            </h3>
            <nav className="space-y-1">
              {lessons.map((lesson, index) => {
                const isCompleted = completedLessons.has(lesson.id);
                const isCurrent = index === currentLessonIndex;
                
                return (
                  <button
                    key={lesson.id}
                    onClick={() => setCurrentLessonIndex(index)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${
                      isCurrent 
                        ? "bg-primary/10 text-primary" 
                        : "hover:bg-muted"
                    }`}
                    data-testid={`lesson-nav-${lesson.id}`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    ) : (
                      <Circle className={`w-5 h-5 shrink-0 ${isCurrent ? "text-primary" : "text-muted-foreground"}`} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isCompleted && !isCurrent ? "text-muted-foreground" : ""}`}>
                        {lesson.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{lesson.duration}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Lesson Content */}
        <Card className="border-border/50">
          <CardContent className="p-6 md:p-8">
            {currentLesson ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Lesson {currentLessonIndex + 1} of {lessons.length}
                    </p>
                    <h2 className="font-heading text-xl font-bold">{currentLesson.title}</h2>
                  </div>
                  {completedLessons.has(currentLesson.id) && (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      Completed
                    </Badge>
                  )}
                </div>

                {/* Markdown Content */}
                <article className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-heading prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3 prose-p:leading-relaxed prose-li:leading-relaxed prose-table:text-sm prose-th:bg-muted prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-xl">
                  <ReactMarkdown>{currentLesson.content}</ReactMarkdown>
                </article>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-10 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentLessonIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentLessonIndex === 0}
                    className="gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>

                  {currentLessonIndex === lessons.length - 1 ? (
                    allComplete ? (
                      <Button
                        onClick={() => navigate("/courses")}
                        className="gap-2"
                        data-testid="finish-course-btn"
                      >
                        <GraduationCap className="w-4 h-4" />
                        Back to Courses
                      </Button>
                    ) : (
                      <Button
                        onClick={handleCompleteLesson}
                        className="gap-2"
                        data-testid="complete-lesson-btn"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Complete Course
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={handleCompleteLesson}
                      className="gap-2"
                      data-testid="next-lesson-btn"
                    >
                      {completedLessons.has(currentLesson.id) ? "Next Lesson" : "Mark Complete & Continue"}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No lessons available for this course yet.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Badge Celebration Modal */}
      <BadgeCelebrationModal 
        badge={celebrationBadge}
        open={!!celebrationBadge}
        onClose={handleCloseCelebration}
      />
    </div>
  );
};
